import React, { useState } from 'react';
import { MemberPasscodePrintModal } from './MemberPasscodePrintModal';
import {
  ArrowLeft,
  Search,
  Plus,
  Edit2,
  Trash2,
  Upload,
  FileText,
  CreditCard,
  Car,
  Heart,
  FileCheck,
  History,
  Info,
  Users,
  ChevronDown,
  Calendar,
  Check,
  X,
  Phone,
  Mail,
  User,
  Shield,
  Eye,
  Download,
  Paperclip,
  Save,
  CheckCircle2,
} from 'lucide-react';

export type UnitSubTab =
  | 'room_info'
  | 'residents'
  | 'keycards'
  | 'vehicles'
  | 'pets'
  | 'ownership'
  | 'history_logs';

interface Props {
  roomNumber: string;
  onBack: () => void;
}

export interface StructuredAddress {
  houseNo: string;
  villageBuilding: string;
  moo: string;
  soi: string;
  road: string;
  subDistrict: string;
  district: string;
  province: string;
  zipcode: string;
}

export interface UnitInfoData {
  unitDeedNumber: string;
  houseNumber: string;
  floorCount: number | string;
  areaSqw: number | string;
  ownershipRatio: number | string;
  status: 'ปล่อยเช่า' | 'อยู่อาศัยเอง' | 'ห้องว่าง' | 'ถูกดำเนินการตามกฎหมาย';
  leaseContractNo: string;
  unitType: string;
  phase: string;
  planCode: string;
  buildingNo: string;
  // Meter & transfer fields
  electricityMeterNo: string;
  waterMeterNo: string;
  transferStatus: 'โอนกรรมสิทธิ์แล้ว' | 'ยังไม่ได้โอนกรรมสิทธิ์';
  transferDate: string;
  ownerName: string;
  moveInDate: string;
  renovationHistory: string;
  // Addresses per role
  addresses: {
    resident: StructuredAddress;
    tenant: StructuredAddress;
    agent: StructuredAddress;
  };
  // Deed & files
  deedFiles: string[];
  otherFiles: string[];
  // Policies
  warrantyPolicy: string;
  warrantyUntilDate: string;
  marketingFeeEnabled: boolean;
  marketingFeeUntilDate: string;
}

export interface UnitResidentItem {
  id: string;
  name: string;
  nameEn?: string;
  username?: string;
  roleType: 'เจ้าของห้อง' | 'ผู้อยู่อาศัย' | 'ผู้เช่า' | 'ตัวแทน';
  isCommittee?: boolean;
  nationality: string;
  ethnicity?: string;
  idCardNo: string;
  phone: string;
  phone2?: string;
  email: string;
  joinDate: string;
  notes?: string;
  idType?: string;
  maritalStatus?: string;
  gender?: string;
  avatarUrl?: string;
  authProvider?: 'google' | 'apple';
  isOnline?: boolean;
}

export interface UnitKeycardItem {
  id: string;
  cardNumber: string;
  cardType: string;
  status: 'active' | 'inactive';
}

export interface UnitVehicleItem {
  id: string;
  vehicleType: 'รถยนต์' | 'รถจักรยานยนต์';
  plateNumber: string;
  province: string;
  brand: string;
  model: string;
  year?: string;
  color?: string;
  seats?: string;
  stickerNo: string;
  floorParking: string;
  slotParking: string;
  startDate: string;
  hasParkingFee: boolean;
  extraDetails?: string;
}

export interface UnitPetItem {
  id: string;
  name: string;
  petType: string;
  description: string;
  healthCertPdf?: string;
  imageUrl?: string;
}

export interface UnitOwnershipHistoryItem {
  id: string;
  transferDate: string;
  roomNumber: string;
  transferor: string[];
  transferee: string[];
  recordedBy: string;
}

export interface UnitResidentHistoryItem {
  id: string;
  orderNo: number;
  name: string;
  nationality: string;
  startDate: string;
  endDate: string;
  reason: string;
  phone: string;
  email: string;
  residentType: 'ผู้อยู่อาศัย' | 'ผู้เช่า' | 'เจ้าของ' | 'ตัวแทน';
  isLatest?: boolean;
}

export const ResidentUnitDetailView: React.FC<Props> = ({ roomNumber = '1086/01', onBack }) => {
  const [activeTab, setActiveTab] = useState<UnitSubTab>('room_info');

  // 1. Room info data with full structured address, meters, transfer, and files
  const [unitInfo, setUnitInfo] = useState<UnitInfoData>({
    unitDeedNumber: '-',
    houseNumber: roomNumber,
    floorCount: 18,
    areaSqw: 125.3,
    ownershipRatio: 1408.7,
    status: 'ปล่อยเช่า',
    leaseContractNo: '123456789',
    unitType: 'บ้านเดี่ยว',
    phase: '2',
    planCode: 'B822',
    buildingNo: 'นิติบุคคล',
    electricityMeterNo: '1268495604',
    waterMeterNo: 'sdfsdfsdf',
    transferStatus: 'โอนกรรมสิทธิ์แล้ว',
    transferDate: '18/02/2022',
    ownerName: 'วีรวิช ทิพยชัยวรุตม์',
    moveInDate: '17/11/2021',
    renovationHistory: 'ติดตั้งแอร์เพิ่ม',
    addresses: {
      resident: {
        houseNo: '8558',
        villageBuilding: 'หมู่ที่ 5',
        moo: '9',
        soi: '9',
        road: 'Jareonkrung',
        subDistrict: 'สุขุมวิท',
        district: 'บางนา',
        province: 'กรุง',
        zipcode: '11130',
      },
      tenant: {
        houseNo: '8558',
        villageBuilding: 'หมู่ที่ 5',
        moo: '9',
        soi: '9',
        road: 'Jareonkrung',
        subDistrict: 'สุขุมวิท',
        district: 'บางนา',
        province: 'กรุง',
        zipcode: '11130',
      },
      agent: {
        houseNo: '8558',
        villageBuilding: 'หมู่ที่ 5',
        moo: '9',
        soi: '9',
        road: 'Jareonkrung',
        subDistrict: 'สุขุมวิท',
        district: 'บางนา',
        province: 'กรุง',
        zipcode: '11130',
      },
    },
    deedFiles: [
      '20297-3256520240202144420.png',
      '20297-5665620220722120014.png',
      '20297-7905720220722115832.png',
    ],
    otherFiles: [
      '20297-4487720220722120029.png',
      '20297-4527620220722120031.png',
      '20297-3773420220819140719.pdf',
    ],
    warrantyPolicy: '-',
    warrantyUntilDate: '-',
    marketingFeeEnabled: true,
    marketingFeeUntilDate: '30/06/2023',
  });

  const [billingTab, setBillingTab] = useState<'resident' | 'tenant' | 'agent'>('resident');
  const [isEditingRoomInfo, setIsEditingRoomInfo] = useState(false);
  const [editFormData, setEditFormData] = useState<UnitInfoData>(unitInfo);
  const [showSaveToast, setShowSaveToast] = useState(false);

  // Address formatter helper
  const formatStructuredAddress = (addr: StructuredAddress) => {
    const parts = [
      addr.houseNo,
      addr.villageBuilding,
      addr.moo ? `หมู่ที่ ${addr.moo}` : '',
      addr.soi ? `ซ.${addr.soi}` : '',
      addr.road ? `ถ.${addr.road}` : '',
      addr.subDistrict ? `ต.${addr.subDistrict}` : '',
      addr.district ? `อ.${addr.district}` : '',
      addr.province ? `จ.${addr.province}` : '',
      addr.zipcode,
    ].filter(Boolean);
    return parts.join(' ');
  };

  const handleStartEdit = () => {
    setEditFormData(JSON.parse(JSON.stringify(unitInfo)));
    setIsEditingRoomInfo(true);
  };

  const handleCancelEdit = () => {
    setEditFormData(JSON.parse(JSON.stringify(unitInfo)));
    setIsEditingRoomInfo(false);
  };

  const handleSaveEdit = () => {
    setUnitInfo(JSON.parse(JSON.stringify(editFormData)));
    setIsEditingRoomInfo(false);
    setShowSaveToast(true);
    setTimeout(() => setShowSaveToast(false), 3000);
  };

  const handleRemoveDeedFile = (idx: number) => {
    setEditFormData((prev) => ({
      ...prev,
      deedFiles: prev.deedFiles.filter((_, i) => i !== idx),
    }));
  };

  const handleAddDeedFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setEditFormData((prev) => ({
        ...prev,
        deedFiles: [...prev.deedFiles, file.name],
      }));
    }
  };

  const handleRemoveOtherFile = (idx: number) => {
    setEditFormData((prev) => ({
      ...prev,
      otherFiles: prev.otherFiles.filter((_, i) => i !== idx),
    }));
  };

  const handleAddOtherFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setEditFormData((prev) => ({
        ...prev,
        otherFiles: [...prev.otherFiles, file.name],
      }));
    }
  };

  // 2. Residents data (matching images 18, 19, 14, etc.)
  const [residentsList, setResidentsList] = useState<UnitResidentItem[]>([
    {
      id: 'res-1',
      name: 'Mr.adisorn kaeophakdee',
      username: 'adisorntest',
      roleType: 'เจ้าของห้อง',
      isCommittee: true,
      nationality: 'AF',
      idCardNo: '-',
      phone: '0000000000',
      email: 'tu140love@gmail.com',
      joinDate: '01/01/1970',
      notes: '-',
      isOnline: true,
    },
    {
      id: 'res-2',
      name: 'คุณjordan andeson',
      username: 'pixal',
      roleType: 'ผู้เช่า',
      nationality: 'TH',
      idCardNo: '0105560101621',
      phone: '09835679497',
      email: 'test2@s.com',
      joinDate: '01/01/1970',
      notes: '-',
      isOnline: true,
    },
    {
      id: 'res-3',
      name: 'คุณxxx xxx',
      username: 'adisorn112211',
      roleType: 'ผู้อยู่อาศัย',
      nationality: 'TH',
      idCardNo: '-',
      phone: '0900000000',
      email: 'tu140love@gmail.com',
      joinDate: '-',
      notes: '-',
      isOnline: true,
    },
    {
      id: 'res-4',
      name: 'คุณweerawit thip',
      username: 'weerawit1',
      roleType: 'ผู้อยู่อาศัย',
      nationality: 'TH',
      idCardNo: '-',
      phone: '0982874827',
      email: 'weerawit@silverman.app',
      joinDate: '-',
      notes: '-',
      isOnline: true,
    },
    {
      id: 'res-5',
      name: 'คุณpatnarin korboon',
      username: 'kaikwan22',
      roleType: 'ผู้อยู่อาศัย',
      nationality: 'TH',
      idCardNo: '-',
      phone: '0841768382',
      email: 'aroundkwan@gmail.com',
      joinDate: '-',
      notes: '-',
      isOnline: true,
    },
    {
      id: 'res-6',
      name: 'คุณadisorn joe',
      username: 'adisorntest23',
      roleType: 'ผู้อยู่อาศัย',
      nationality: 'TH',
      idCardNo: '-',
      phone: '08888888888',
      email: 'tu140love@gmail.com',
      joinDate: '-',
      notes: '-',
      isOnline: true,
    },
    {
      id: 'res-7',
      name: 'คุณวีรวิช ทิพยชัยวรุตม์',
      roleType: 'ผู้อยู่อาศัย',
      nationality: 'SE',
      idCardNo: '1111111111111',
      phone: '0955955425',
      email: 'weerawit@silverman.app',
      joinDate: '-',
      notes: '-',
      authProvider: 'google',
      isOnline: true,
    },
    {
      id: 'res-8',
      name: 'นางสาวPatnarin K',
      roleType: 'ผู้อยู่อาศัย',
      nationality: 'SE',
      idCardNo: '1509901539871',
      phone: '-',
      email: 'patnarin@silverman.app',
      joinDate: '-',
      notes: '-',
      authProvider: 'apple',
      isOnline: true,
    },
    {
      id: 'res-9',
      name: 'คุณadisorn kdp',
      username: 'adisorntest13',
      roleType: 'ผู้อยู่อาศัย',
      nationality: 'TH',
      idCardNo: '-',
      phone: '0888888888',
      email: 'tu140love@gmail.com',
      joinDate: '-',
      notes: '-',
      isOnline: true,
    },
  ]);

  const [showResidentModal, setShowResidentModal] = useState(false);
  const [editingResident, setEditingResident] = useState<UnitResidentItem | null>(null);

  // 3. Keycards data (matching image 27, 28)
  const [keycardsList, setKeycardsList] = useState<UnitKeycardItem[]>([
    { id: 'kc-1', cardNumber: '12111', cardType: 'เข้าห้อง', status: 'active' },
    { id: 'kc-2', cardNumber: '123456789', cardType: 'เข้าห้อง', status: 'active' },
    { id: 'kc-3', cardNumber: '9999999', cardType: 'Parking Car', status: 'active' },
    { id: 'kc-4', cardNumber: 'ED364578', cardType: 'ใหม่', status: 'active' },
    { id: 'kc-5', cardNumber: '445566', cardType: 'TEST Noina', status: 'active' },
    { id: 'kc-6', cardNumber: '1234567', cardType: 'เข้าออก', status: 'active' },
    { id: 'kc-7', cardNumber: '1234456', cardType: 'test', status: 'active' },
    { id: 'kc-8', cardNumber: '098998', cardType: 'test', status: 'active' },
  ]);
  const [showAddKeycardModal, setShowAddKeycardModal] = useState(false);
  const [newKeycardType, setNewKeycardType] = useState('');
  const [newKeycardNumber, setNewKeycardNumber] = useState('');

  // 4. Vehicles data (matching image 29, 30)
  const [vehiclesList, setVehiclesList] = useState<UnitVehicleItem[]>([
    {
      id: 'veh-1',
      vehicleType: 'รถยนต์',
      plateNumber: '1113',
      province: 'กรุงเทพมหานคร',
      brand: 'BMW',
      model: 'BMW 4 Series Coupe ดำ',
      stickerNo: '444444',
      floorParking: '-',
      slotParking: '-',
      startDate: '2024-01-01',
      hasParkingFee: true,
    },
    {
      id: 'veh-2',
      vehicleType: 'รถยนต์',
      plateNumber: 'รฟ 3453',
      province: 'ชลบุรี',
      brand: 'Honda',
      model: 'City Rs ดำ',
      stickerNo: 'AVC4456689',
      floorParking: '2',
      slotParking: '10',
      startDate: '2024-02-15',
      hasParkingFee: true,
    },
    {
      id: 'veh-3',
      vehicleType: 'รถยนต์',
      plateNumber: '2กด555555',
      province: 'ลพบุรี',
      brand: 'Toyota',
      model: '5555',
      stickerNo: '-',
      floorParking: '-',
      slotParking: '-',
      startDate: '2024-03-01',
      hasParkingFee: true,
    },
  ]);
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);
  const [newVehType, setNewVehType] = useState<'รถยนต์' | 'รถจักรยานยนต์'>('รถยนต์');
  const [newVehPlate, setNewVehPlate] = useState('');
  const [newVehProv, setNewVehProv] = useState('กรุงเทพมหานคร');
  const [newVehBrand, setNewVehBrand] = useState('');
  const [newVehModel, setNewVehModel] = useState('');
  const [newVehYear, setNewVehYear] = useState('');
  const [newVehSeats, setNewVehSeats] = useState('');
  const [newVehSticker, setNewVehSticker] = useState('');
  const [newVehFloor, setNewVehFloor] = useState('');
  const [newVehSlot, setNewVehSlot] = useState('');
  const [newVehDate, setNewVehDate] = useState('2024-03-29');
  const [newVehFee, setNewVehFee] = useState(true);

  // 5. Pets data (matching image 31, 32)
  const [petsList, setPetsList] = useState<UnitPetItem[]>([
    {
      id: 'pet-1',
      name: 'น้องแมว',
      petType: 'แมว',
      description: '99/99 ชื่อเฌอแตม ตัวสีดำล้วน ท้องมีสีขาว และปลายเท้ามีสีขาว หางยาว มีปลอกคอ มีชื่อ และเบอร์โทร 061-679-4644',
      healthCertPdf: 'ใบตรวจสุขภาพประจำปี',
    },
    {
      id: 'pet-2',
      name: 'แมวเหมียว',
      petType: 'แมว',
      description: 'อ้วนกลม',
      healthCertPdf: '20297-7749820230307214623.pdf',
    },
    {
      id: 'pet-3',
      name: 'หนูนา',
      petType: 'หนูแฮมเตอร์',
      description: 'หูใหญ่',
      healthCertPdf: '-',
    },
    {
      id: 'pet-4',
      name: 'ปลากัด น้องถุงเงิน',
      petType: 'ปลากัด',
      description: 'สีน้ำเงิน',
      healthCertPdf: '-',
    },
    {
      id: 'pet-5',
      name: 'ไก่ชนบุรี',
      petType: 'ไก่',
      description: 'สีดำ-แดง',
      healthCertPdf: '-',
    },
    {
      id: 'pet-6',
      name: 'นานาจ๋า',
      petType: 'แมว',
      description: 'ดื้อ',
      healthCertPdf: '-',
    },
  ]);
  const [showPetModal, setShowPetModal] = useState(false);
  const [editingPet, setEditingPet] = useState<UnitPetItem | null>(null);
  const [newPetType, setNewPetType] = useState('');
  const [newPetDesc, setNewPetDesc] = useState('');

  // 6. Ownership History data (matching image 33, 35)
  const [ownershipHistories, setOwnershipHistories] = useState<UnitOwnershipHistoryItem[]>([
    {
      id: 'own-1',
      transferDate: '2021-11-07',
      roomNumber: roomNumber,
      transferor: ['adisorn kaeophakdee', 'nikki rungchamras', 'อ้อย มะระ', 'สวัสดีวันศุกร์', 'tob tob'],
      transferee: ['test test'],
      recordedBy: 'silverman support',
    },
    {
      id: 'own-2',
      transferDate: '2021-11-23',
      roomNumber: roomNumber,
      transferor: ['มะหงิก ไก่แจ้', 'test test'],
      transferee: ['test test', 'เด็กดี มีสี'],
      recordedBy: 'silverman support',
    },
    {
      id: 'own-3',
      transferDate: '2021-11-26',
      roomNumber: roomNumber,
      transferor: ['เด็กดี มีสี', 'ฟ่าะพะ กเฟาทๆ', 'test เสมอภาค'],
      transferee: ['test เสมอภาค', 'อิลิค โลโซ'],
      recordedBy: 'silverman support',
    },
    {
      id: 'own-4',
      transferDate: '2021-12-09',
      roomNumber: roomNumber,
      transferor: ['อิลิค โลโซ'],
      transferee: ['test test'],
      recordedBy: 'silverman support',
    },
    {
      id: 'own-5',
      transferDate: '2022-02-04',
      roomNumber: roomNumber,
      transferor: ['Nitcha Phanunan', 'ทิพวรรณ อาจเลิศ', 'ดึก ดำดึ๋ย'],
      transferee: ['ทิพวรรณ อาจเลิศ', 'ดึก ดำดึ๋ย', 'นางสาวนัฏฐา ผาสุริหาโร', 'silverman test'],
      recordedBy: 'silverman support',
    },
  ]);
  const [showTransferModal, setShowTransferModal] = useState(false);

  // 7. Unit Resident History Logs (matching image 36, 26)
  const [residentHistories] = useState<UnitResidentHistoryItem[]>([
    {
      id: 'rh-1',
      orderNo: 140,
      name: 'adisorn kaeophakdee',
      nationality: 'TH',
      startDate: '01/06/2023',
      endDate: '-',
      reason: '-',
      phone: '0000000000',
      email: 'tu140love@gmail.com',
      residentType: 'ผู้อยู่อาศัย',
      isLatest: true,
    },
    {
      id: 'rh-2',
      orderNo: 139,
      name: 'meaw meaw',
      nationality: 'CH',
      startDate: '18/11/2022',
      endDate: '21/03/2024',
      reason: 'หมดสัญญารายเดือน / ปี',
      phone: '-',
      email: '-',
      residentType: 'ผู้อยู่อาศัย',
    },
    {
      id: 'rh-3',
      orderNo: 138,
      name: 'AAA BBB',
      nationality: 'TH',
      startDate: '25/10/2022',
      endDate: '21/03/2024',
      reason: 'หมดสัญญารายเดือน / ปี',
      phone: '-',
      email: '-',
      residentType: 'ผู้เช่า',
    },
    {
      id: 'rh-4',
      orderNo: 137,
      name: 'ddll lldd',
      nationality: 'TH',
      startDate: '10/09/2022',
      endDate: '21/03/2024',
      reason: 'หมดสัญญารายเดือน / ปี',
      phone: '0895374155',
      email: 'kantawee@silverman.app',
      residentType: 'ผู้อยู่อาศัย',
    },
    {
      id: 'rh-5',
      orderNo: 136,
      name: 'maduo maduo',
      nationality: 'TH',
      startDate: '06/07/2022',
      endDate: '21/03/2024',
      reason: 'หมดสัญญารายเดือน / ปี',
      phone: '0986536769',
      email: 'fgh@c.c',
      residentType: 'ผู้อยู่อาศัย',
    },
    {
      id: 'rh-6',
      orderNo: 135,
      name: 'sa sa',
      nationality: 'TH',
      startDate: '07/06/2022',
      endDate: '21/03/2024',
      reason: 'หมดสัญญารายเดือน / ปี',
      phone: '0123456789',
      email: 'salavut@silverman.app',
      residentType: 'ผู้อยู่อาศัย',
    },
    {
      id: 'rh-7',
      orderNo: 134,
      name: 'gjk bjzks',
      nationality: 'TH',
      startDate: '13/09/2022',
      endDate: '21/03/2024',
      reason: 'หมดสัญญารายเดือน / ปี',
      phone: '1234567890',
      email: 'saravut.phikulngoen@gmail.com',
      residentType: 'ผู้เช่า',
    },
    {
      id: 'rh-8',
      orderNo: 133,
      name: 'tata3 tata3',
      nationality: 'TH',
      startDate: '29/03/2023',
      endDate: '21/03/2024',
      reason: 'หมดสัญญารายเดือน / ปี',
      phone: '1234567890',
      email: 'kk@gn.com',
      residentType: 'ผู้อยู่อาศัย',
    },
    {
      id: 'rh-9',
      orderNo: 132,
      name: 'เอ.บี.พี. เวลลอปเมนท์ จำกัด (มหาชน)',
      nationality: 'TH',
      startDate: '06/09/2022',
      endDate: '21/03/2024',
      reason: 'หมดสัญญารายเดือน / ปี',
      phone: '-',
      email: '-',
      residentType: 'ผู้อยู่อาศัย',
    },
    {
      id: 'rh-10',
      orderNo: 131,
      name: 'ดึก ดำดึ๋ย',
      nationality: 'TH',
      startDate: '18/02/2022',
      endDate: '21/03/2024',
      reason: 'หมดสัญญารายเดือน / ปี',
      phone: '089-9999999',
      email: '-',
      residentType: 'ผู้อยู่อาศัย',
    },
  ]);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [historySearch, setHistorySearch] = useState('');

  // Passcode modal state for specific unit
  const [showSinglePasscodeModal, setShowSinglePasscodeModal] = useState(false);
  const [selectedPasscodeRole, setSelectedPasscodeRole] = useState<'ผู้อยู่อาศัย' | 'ผู้เช่า' | 'ตัวแทน'>('ผู้อยู่อาศัย');

  // Subtabs configuration
  const subTabs = [
    { key: 'room_info', label: 'ข้อมูลห้อง' },
    { key: 'residents', label: 'ผู้อยู่อาศัย' },
    { key: 'keycards', label: 'คีย์การ์ด' },
    { key: 'vehicles', label: 'ยานพาหนะ' },
    { key: 'pets', label: 'สัตว์เลี้ยง' },
    { key: 'ownership', label: 'กรรมสิทธิ์' },
    { key: 'history_logs', label: 'ประวัติผู้อยู่อาศัย' },
  ];

  // Helper to handle add keycard
  const handleSaveKeycard = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKeycardNumber) return;
    const newKc: UnitKeycardItem = {
      id: `kc-${Date.now()}`,
      cardNumber: newKeycardNumber,
      cardType: newKeycardType || 'เข้าห้อง',
      status: 'active',
    };
    setKeycardsList([...keycardsList, newKc]);
    setShowAddKeycardModal(false);
    setNewKeycardNumber('');
    setNewKeycardType('');
  };

  // Helper to handle add vehicle
  const handleSaveVehicle = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newVehPlate) return;
    const newV: UnitVehicleItem = {
      id: `veh-${Date.now()}`,
      vehicleType: newVehType,
      plateNumber: newVehPlate,
      province: newVehProv,
      brand: newVehBrand || 'Toyota',
      model: newVehModel || 'Corolla',
      stickerNo: newVehSticker || '-',
      floorParking: newVehFloor || '-',
      slotParking: newVehSlot || '-',
      startDate: newVehDate,
      hasParkingFee: newVehFee,
    };
    setVehiclesList([...vehiclesList, newV]);
    setShowAddVehicleModal(false);
    setNewVehPlate('');
    setNewVehBrand('');
    setNewVehModel('');
  };

  return (
    <div className="font-prompt bg-white min-h-[85vh] rounded-2xl shadow-xs border border-slate-200/80 overflow-hidden">
      {/* 1. Header Bar with Back Button & Room Number */}
      <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between bg-white">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="w-7 h-7 rounded-full border border-slate-300 hover:border-slate-400 bg-white flex items-center justify-center text-slate-600 hover:text-slate-900 transition-colors cursor-pointer"
            title="ย้อนกลับไปทะเบียนลูกบ้าน"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 tracking-tight">
            {roomNumber}
          </h1>
        </div>

        {activeTab === 'ownership' && (
          <button
            type="button"
            onClick={() => setShowTransferModal(true)}
            className="px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-xs font-semibold flex items-center gap-2 shadow-xs transition-colors cursor-pointer"
          >
            <Plus className="w-4 h-4" /> เปลี่ยนแปลงกรรมสิทธิ์
          </button>
        )}
      </div>

      {/* 2. Sub-Tabs Bar */}
      <div className="border-b border-slate-200 bg-slate-50/50 px-6 pt-3 overflow-x-auto">
        <div className="flex items-center gap-8 min-w-max text-xs sm:text-sm">
          {subTabs.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key as UnitSubTab)}
                className={`pb-3 font-medium transition-colors cursor-pointer relative ${
                  isActive
                    ? 'text-slate-900 font-bold border-b-2 border-sky-600'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* 3. Main Tab Contents */}
      <div className="p-6">
        {/* ========================================================================= */}
        {/* TAB 1: ข้อมูลห้อง (Room Info) matching image 1, 2, 3, 4, 5 */}
        {/* ========================================================================= */}
        {activeTab === 'room_info' && (
          <div className="space-y-6">
            {/* Top Edit / Save Action Bar */}
            {isEditingRoomInfo && (
              <div className="bg-sky-50 border border-sky-200 rounded-xl p-3.5 flex items-center justify-between shadow-2xs animate-in fade-in">
                <span className="text-xs font-semibold text-sky-900 flex items-center gap-2">
                  <Edit2 className="w-4 h-4 text-sky-600" /> โหมดแก้ไขข้อมูลห้อง {roomNumber}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="px-3.5 py-1.5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 rounded-lg text-xs font-medium transition-colors cursor-pointer"
                  >
                    ยกเลิก
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveEdit}
                    className="px-4 py-1.5 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow-xs transition-colors cursor-pointer"
                  >
                    <Save className="w-3.5 h-3.5" /> บันทึกข้อมูล
                  </button>
                </div>
              </div>
            )}

            {/* Notification Toast */}
            {showSaveToast && (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2.5 rounded-xl text-xs font-medium flex items-center gap-2 shadow-sm animate-in fade-in">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                บันทึกการแก้ไขข้อมูลห้องเรียบร้อยแล้ว
              </div>
            )}

            {/* If NOT editing -> View Mode (Matching Image 1) */}
            {!isEditingRoomInfo ? (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Left Column: ข้อมูลทั่วไป + ข้อมูลมิเตอร์ + ประวัติการตกแต่ง */}
                <div className="lg:col-span-7 space-y-6">
                  {/* Card 1: ข้อมูลทั่วไป */}
                  <div className="bg-white rounded-2xl border border-slate-200/90 p-6 shadow-2xs">
                    <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
                      <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
                        ข้อมูลทั่วไป
                      </h3>
                      <button
                        type="button"
                        onClick={handleStartEdit}
                        className="text-xs text-slate-500 hover:text-sky-600 flex items-center gap-1 cursor-pointer transition-colors"
                      >
                        <Edit2 className="w-3.5 h-3.5" /> แก้ไข
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 text-xs">
                      <div>
                        <span className="text-slate-400 block mb-1">ห้องชุดเลขที่</span>
                        <span className="font-medium text-slate-800">{unitInfo.unitDeedNumber}</span>
                      </div>

                      <div>
                        <span className="text-slate-400 block mb-1">บ้านเลขที่</span>
                        <span className="font-bold text-slate-900">{unitInfo.houseNumber}</span>
                      </div>

                      <div className="sm:col-span-2">
                        <span className="text-slate-400 block mb-1">สร้างรหัสสมาชิก</span>
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedPasscodeRole('ผู้อยู่อาศัย');
                              setShowSinglePasscodeModal(true);
                            }}
                            className="text-sky-600 hover:underline font-medium cursor-pointer"
                          >
                            ผู้อยู่อาศัย
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedPasscodeRole('ผู้เช่า');
                              setShowSinglePasscodeModal(true);
                            }}
                            className="text-sky-600 hover:underline font-medium cursor-pointer"
                          >
                            ผู้เช่า
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedPasscodeRole('ตัวแทน');
                              setShowSinglePasscodeModal(true);
                            }}
                            className="text-sky-600 hover:underline font-medium cursor-pointer"
                          >
                            ตัวแทน
                          </button>
                        </div>
                      </div>

                      <div>
                        <span className="text-slate-400 block mb-1">จำนวนชั้น</span>
                        <span className="font-medium text-slate-800">{unitInfo.floorCount}</span>
                      </div>

                      <div>
                        <span className="text-slate-400 block mb-1">ขนาดพื้นที่</span>
                        <span className="font-medium text-slate-800">{unitInfo.areaSqw}ตร.ว.</span>
                      </div>

                      <div>
                        <span className="text-slate-400 block mb-1">อัตราส่วนกรรมสิทธิ์</span>
                        <span className="font-medium text-slate-800">{unitInfo.ownershipRatio}</span>
                      </div>

                      <div>
                        <span className="text-slate-400 block mb-1">สถานะ</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-sky-50 text-sky-700 border border-sky-200">
                          {unitInfo.status}
                        </span>
                      </div>

                      <div>
                        <span className="text-slate-400 block mb-1">เลขที่สัญญาเช่า</span>
                        <span className="font-medium text-slate-800">{unitInfo.leaseContractNo}</span>
                      </div>

                      <div>
                        <span className="text-slate-400 block mb-1">สัญญาเช่า</span>
                        <button
                          type="button"
                          className="text-sky-600 hover:underline flex items-center gap-1 font-medium cursor-pointer"
                        >
                          <FileText className="w-3.5 h-3.5" /> ดูเอกสาร
                        </button>
                      </div>

                      <div>
                        <span className="text-slate-400 block mb-1">ประเภท</span>
                        <span className="font-medium text-slate-800">{unitInfo.unitType}</span>
                      </div>

                      <div>
                        <span className="text-slate-400 block mb-1">เฟส</span>
                        <span className="font-medium text-slate-800">{unitInfo.phase}</span>
                      </div>

                      <div>
                        <span className="text-slate-400 block mb-1">แปลน</span>
                        <span className="font-medium text-slate-800">{unitInfo.planCode}</span>
                      </div>

                      <div>
                        <span className="text-slate-400 block mb-1">เลขอาคาร</span>
                        <span className="font-medium text-slate-800">{unitInfo.buildingNo}</span>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: ข้อมูลมิเตอร์และโอนกรรมสิทธิ์ (View Mode) */}
                  <div className="bg-white rounded-2xl border border-slate-200/90 p-6 shadow-2xs">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 text-xs">
                      <div>
                        <span className="text-slate-400 block mb-1">หมายเลขเครื่องมิเตอร์ไฟ</span>
                        <span className="font-medium text-slate-800">{unitInfo.electricityMeterNo}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block mb-1">หมายเลขเครื่องมิเตอร์น้ำ</span>
                        <span className="font-medium text-slate-800">{unitInfo.waterMeterNo}</span>
                      </div>
                      <div className="sm:col-span-2">
                        <span className="text-slate-400 block mb-1">สถานะการโอนจากบริษัทผู้พัฒนา</span>
                        <span className="inline-flex items-center gap-1.5 font-medium text-slate-800">
                          <span className="w-2.5 h-2.5 rounded-full bg-sky-500" />
                          {unitInfo.transferStatus}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-400 block mb-1">วันที่โอนกรรมสิทธิ์ล่าสุด</span>
                        <span className="font-medium text-slate-800">{unitInfo.transferDate}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block mb-1">เจ้าของกรรมสิทธิ์</span>
                        <span className="font-medium text-slate-800">{unitInfo.ownerName}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block mb-1">วันที่เข้าอยู่</span>
                        <span className="font-medium text-slate-800 flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-slate-400" />
                          {unitInfo.moveInDate}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Card 3: ประวัติการตกแต่ง (View Mode) */}
                  <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs">
                    <span className="text-slate-400 text-xs block mb-1">ประวัติการตกแต่งถ้ามี</span>
                    <span className="text-xs font-medium text-slate-800">
                      {unitInfo.renovationHistory || '-'}
                    </span>
                  </div>
                </div>

                {/* Right Column: ที่อยู่ในการออกเอกสาร & เอกสารโฉนด & นโยบาย */}
                <div className="lg:col-span-5 space-y-6">
                  {/* Card 1: ที่อยู่ในการออกเอกสาร */}
                  <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs">
                    <h3 className="font-bold text-slate-800 text-xs sm:text-sm mb-3">
                      ที่อยู่ในการออกเอกสาร
                    </h3>

                    <div className="flex items-center gap-4 border-b border-slate-100 pb-2 text-xs mb-3">
                      <button
                        type="button"
                        onClick={() => setBillingTab('resident')}
                        className={`pb-1 cursor-pointer font-medium ${
                          billingTab === 'resident'
                            ? 'text-sky-600 border-b-2 border-sky-600 font-bold'
                            : 'text-slate-400 hover:text-slate-700'
                        }`}
                      >
                        ผู้อยู่อาศัย/เจ้าของร่วม
                      </button>
                      <button
                        type="button"
                        onClick={() => setBillingTab('tenant')}
                        className={`pb-1 cursor-pointer font-medium ${
                          billingTab === 'tenant'
                            ? 'text-sky-600 border-b-2 border-sky-600 font-bold'
                            : 'text-slate-400 hover:text-slate-700'
                        }`}
                      >
                        ผู้เช่า
                      </button>
                      <button
                        type="button"
                        onClick={() => setBillingTab('agent')}
                        className={`pb-1 cursor-pointer font-medium ${
                          billingTab === 'agent'
                            ? 'text-sky-600 border-b-2 border-sky-600 font-bold'
                            : 'text-slate-400 hover:text-slate-700'
                        }`}
                      >
                        ตัวแทน
                      </button>
                    </div>

                    <p className="text-xs text-slate-600 leading-relaxed bg-slate-50 p-3 rounded-xl border border-slate-100">
                      {formatStructuredAddress(unitInfo.addresses[billingTab])}
                    </p>
                  </div>

                  {/* Card 2: เอกสารโฉนดและอื่นๆ */}
                  <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs">
                    <h3 className="font-bold text-slate-800 text-xs sm:text-sm mb-3">
                      เอกสารโฉนดและอื่นๆ
                    </h3>

                    <div className="space-y-3 text-xs">
                      <div>
                        <span className="text-slate-500 font-semibold block mb-1.5">โฉนด</span>
                        <div className="space-y-1.5">
                          {unitInfo.deedFiles.map((file, idx) => (
                            <div
                              key={idx}
                              className="flex items-center justify-between p-2 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-100 text-slate-700 transition-colors"
                            >
                              <span className="flex items-center gap-2 truncate text-slate-600">
                                <span className="w-6 h-6 rounded bg-emerald-100 text-emerald-700 text-[10px] font-bold flex items-center justify-center shrink-0">
                                  PNG
                                </span>
                                <span className="truncate">{file}</span>
                              </span>
                              <button
                                type="button"
                                className="text-sky-600 hover:text-sky-800 p-1"
                                title="ดาวน์โหลด"
                              >
                                <Download className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="pt-2 border-t border-slate-100">
                        <span className="text-slate-500 font-semibold block mb-1.5">เอกสารอื่นๆ</span>
                        <div className="space-y-1.5">
                          {unitInfo.otherFiles.map((file, idx) => (
                            <div
                              key={idx}
                              className="flex items-center justify-between p-2 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-100 text-slate-700 transition-colors"
                            >
                              <span className="flex items-center gap-2 truncate text-slate-600">
                                <span
                                  className={`w-6 h-6 rounded text-[10px] font-bold flex items-center justify-center shrink-0 ${
                                    file.endsWith('.pdf')
                                      ? 'bg-rose-100 text-rose-700'
                                      : 'bg-emerald-100 text-emerald-700'
                                  }`}
                                >
                                  {file.endsWith('.pdf') ? 'PDF' : 'PNG'}
                                </span>
                                <span className="truncate">{file}</span>
                              </span>
                              <button
                                type="button"
                                className="text-sky-600 hover:text-sky-800 p-1"
                                title="ดาวน์โหลด"
                              >
                                <Download className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 3: นโยบายการรับประกัน & นโยบายทางการตลาด (View Mode) */}
                  <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs text-xs space-y-4">
                    <div>
                      <div className="flex items-center justify-between text-slate-400 font-medium mb-1 border-b border-slate-100 pb-1">
                        <span>นโยบายการรับประกัน</span>
                        <span>จนถึงวันที่</span>
                      </div>
                      <div className="flex items-center justify-between text-slate-700 font-medium py-1">
                        <span>{unitInfo.warrantyPolicy}</span>
                        <span>{unitInfo.warrantyUntilDate}</span>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-slate-100">
                      <div className="flex items-center justify-between text-slate-400 font-medium mb-1 border-b border-slate-100 pb-1">
                        <span>นโยบายทางการตลาด</span>
                        <span>จนถึงวันที่</span>
                      </div>
                      <div className="flex items-center justify-between text-slate-700 font-medium py-1">
                        <span className="flex items-center gap-2">
                          <span className="w-3.5 h-3.5 rounded bg-sky-100 text-sky-600 flex items-center justify-center text-[10px]">
                            ✓
                          </span>
                          ค่าส่วนกลาง / common fee
                        </span>
                        <span>{unitInfo.marketingFeeUntilDate}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* ======================================================================= */
              /* EDIT MODE (Matching Image 2, 3, 4, 5) */
              /* ======================================================================= */
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 animate-in fade-in">
                {/* Left Column: ข้อมูลห้อง Form + ข้อมูลมิเตอร์ Form + ประวัติการตกแต่ง Form */}
                <div className="lg:col-span-7 space-y-6">
                  {/* Card 1: ข้อมูลห้อง (Image 2 & 3) */}
                  <div className="bg-white rounded-2xl border border-slate-200/90 p-6 shadow-2xs space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 text-xs">
                      <div>
                        <label className="text-slate-500 font-medium block mb-1">ห้องชุดเลขที่</label>
                        <span className="text-slate-800 font-medium block py-1.5">{editFormData.unitDeedNumber}</span>
                      </div>

                      <div>
                        <label className="text-slate-500 font-medium block mb-1">บ้านเลขที่</label>
                        <span className="text-slate-900 font-bold block py-1.5">{editFormData.houseNumber}</span>
                      </div>

                      <div>
                        <label className="text-slate-600 font-medium block mb-1">จำนวนชั้น</label>
                        <input
                          type="text"
                          value={editFormData.floorCount}
                          onChange={(e) => setEditFormData({ ...editFormData, floorCount: e.target.value })}
                          className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        />
                      </div>

                      <div>
                        <label className="text-slate-600 font-medium block mb-1">ขนาดพื้นที่</label>
                        <input
                          type="text"
                          value={editFormData.areaSqw}
                          onChange={(e) => setEditFormData({ ...editFormData, areaSqw: e.target.value })}
                          className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        />
                      </div>

                      <div>
                        <label className="text-slate-600 font-medium block mb-1">อัตราส่วนกรรมสิทธิ์</label>
                        <input
                          type="text"
                          value={editFormData.ownershipRatio}
                          onChange={(e) => setEditFormData({ ...editFormData, ownershipRatio: e.target.value })}
                          className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        />
                      </div>

                      <div>
                        <label className="text-slate-600 font-medium block mb-1">สถานะ</label>
                        <select
                          value={editFormData.status}
                          onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value as any })}
                          className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500 bg-white"
                        >
                          <option value="ปล่อยเช่า">ปล่อยเช่า</option>
                          <option value="อยู่อาศัยเอง">อยู่อาศัยเอง</option>
                          <option value="ห้องว่าง">ห้องว่าง</option>
                          <option value="ถูกดำเนินการตามกฎหมาย">ถูกดำเนินการตามกฎหมาย</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-slate-600 font-medium block mb-1">เลขที่สัญญาเช่า</label>
                        <input
                          type="text"
                          value={editFormData.leaseContractNo}
                          onChange={(e) => setEditFormData({ ...editFormData, leaseContractNo: e.target.value })}
                          className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        />
                      </div>

                      <div>
                        <label className="text-slate-600 font-medium block mb-1">ประเภท</label>
                        <input
                          type="text"
                          value={editFormData.unitType}
                          onChange={(e) => setEditFormData({ ...editFormData, unitType: e.target.value })}
                          className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        />
                      </div>

                      <div>
                        <label className="text-slate-600 font-medium block mb-1">เฟส</label>
                        <input
                          type="text"
                          value={editFormData.phase}
                          onChange={(e) => setEditFormData({ ...editFormData, phase: e.target.value })}
                          className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        />
                      </div>

                      <div>
                        <label className="text-slate-600 font-medium block mb-1">แปลน</label>
                        <input
                          type="text"
                          value={editFormData.planCode}
                          onChange={(e) => setEditFormData({ ...editFormData, planCode: e.target.value })}
                          className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="text-slate-600 font-medium block mb-1">เลขอาคาร</label>
                        <input
                          type="text"
                          value={editFormData.buildingNo}
                          onChange={(e) => setEditFormData({ ...editFormData, buildingNo: e.target.value })}
                          className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Card 2: ข้อมูลมิเตอร์และโอนกรรมสิทธิ์ Form (Image 3) */}
                  <div className="bg-white rounded-2xl border border-slate-200/90 p-6 shadow-2xs space-y-4">
                    <div className="space-y-4 text-xs">
                      <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center">
                        <label className="sm:col-span-4 text-slate-600 font-medium">หมายเลขเครื่องมิเตอร์ไฟ</label>
                        <div className="sm:col-span-8">
                          <input
                            type="text"
                            value={editFormData.electricityMeterNo}
                            onChange={(e) => setEditFormData({ ...editFormData, electricityMeterNo: e.target.value })}
                            className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center">
                        <label className="sm:col-span-4 text-slate-600 font-medium">หมายเลขเครื่องมิเตอร์น้ำ</label>
                        <div className="sm:col-span-8">
                          <input
                            type="text"
                            value={editFormData.waterMeterNo}
                            onChange={(e) => setEditFormData({ ...editFormData, waterMeterNo: e.target.value })}
                            className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center">
                        <label className="sm:col-span-4 text-slate-600 font-medium">สถานะการโอนจากบริษัทผู้พัฒนา</label>
                        <div className="sm:col-span-8 flex items-center gap-6">
                          <label className="flex items-center gap-2 cursor-pointer text-slate-700">
                            <input
                              type="radio"
                              name="transferStatusEdit"
                              checked={editFormData.transferStatus === 'โอนกรรมสิทธิ์แล้ว'}
                              onChange={() => setEditFormData({ ...editFormData, transferStatus: 'โอนกรรมสิทธิ์แล้ว' })}
                              className="text-sky-600 focus:ring-sky-500"
                            />
                            <span>โอนกรรมสิทธิ์แล้ว</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer text-slate-700">
                            <input
                              type="radio"
                              name="transferStatusEdit"
                              checked={editFormData.transferStatus === 'ยังไม่ได้โอนกรรมสิทธิ์'}
                              onChange={() => setEditFormData({ ...editFormData, transferStatus: 'ยังไม่ได้โอนกรรมสิทธิ์' })}
                              className="text-sky-600 focus:ring-sky-500"
                            />
                            <span>ยังไม่ได้โอนกรรมสิทธิ์</span>
                          </label>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center">
                        <label className="sm:col-span-4 text-slate-600 font-medium">วันที่โอนกรรมสิทธิ์ล่าสุด</label>
                        <div className="sm:col-span-8">
                          <input
                            type="text"
                            value={editFormData.transferDate}
                            onChange={(e) => setEditFormData({ ...editFormData, transferDate: e.target.value })}
                            className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center">
                        <label className="sm:col-span-4 text-slate-600 font-medium">เจ้าของกรรมสิทธิ์</label>
                        <div className="sm:col-span-8">
                          <input
                            type="text"
                            value={editFormData.ownerName}
                            onChange={(e) => setEditFormData({ ...editFormData, ownerName: e.target.value })}
                            className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center">
                        <label className="sm:col-span-4 text-slate-600 font-medium">วันที่เข้าอยู่</label>
                        <div className="sm:col-span-8 relative">
                          <input
                            type="text"
                            value={editFormData.moveInDate}
                            onChange={(e) => setEditFormData({ ...editFormData, moveInDate: e.target.value })}
                            className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500 pr-9"
                          />
                          <button
                            type="button"
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          >
                            <Calendar className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 3: ประวัติการตกแต่ง Form (Image 3 & 4) */}
                  <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs">
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center text-xs">
                      <label className="sm:col-span-4 text-slate-600 font-medium">ประวัติการตกแต่งถ้ามี</label>
                      <div className="sm:col-span-8">
                        <input
                          type="text"
                          value={editFormData.renovationHistory}
                          onChange={(e) => setEditFormData({ ...editFormData, renovationHistory: e.target.value })}
                          className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column: ที่อยู่ในการออกเอกสาร Edit + แนบเอกสารประกอบโฉนด & อื่นๆ + นโยบาย */}
                <div className="lg:col-span-5 space-y-6">
                  {/* Card 1: ที่อยู่ในการออกเอกสาร Form (Image 2 & 3) */}
                  <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs">
                    <h3 className="font-bold text-slate-800 text-xs sm:text-sm mb-3">
                      ที่อยู่ในการออกเอกสาร
                    </h3>

                    <div className="flex items-center gap-4 border-b border-slate-100 pb-2 text-xs mb-4">
                      <button
                        type="button"
                        onClick={() => setBillingTab('resident')}
                        className={`pb-1 cursor-pointer font-medium ${
                          billingTab === 'resident'
                            ? 'text-sky-600 border-b-2 border-sky-600 font-bold'
                            : 'text-slate-400 hover:text-slate-700'
                        }`}
                      >
                        ผู้อยู่อาศัย/เจ้าของร่วม
                      </button>
                      <button
                        type="button"
                        onClick={() => setBillingTab('tenant')}
                        className={`pb-1 cursor-pointer font-medium ${
                          billingTab === 'tenant'
                            ? 'text-sky-600 border-b-2 border-sky-600 font-bold'
                            : 'text-slate-400 hover:text-slate-700'
                        }`}
                      >
                        ผู้เช่า
                      </button>
                      <button
                        type="button"
                        onClick={() => setBillingTab('agent')}
                        className={`pb-1 cursor-pointer font-medium ${
                          billingTab === 'agent'
                            ? 'text-sky-600 border-b-2 border-sky-600 font-bold'
                            : 'text-slate-400 hover:text-slate-700'
                        }`}
                      >
                        ตัวแทน
                      </button>
                    </div>

                    {/* 2-Column Address Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                      <div>
                        <label className="text-slate-600 font-medium block mb-1">เลขที่</label>
                        <input
                          type="text"
                          value={editFormData.addresses[billingTab].houseNo}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              addresses: {
                                ...editFormData.addresses,
                                [billingTab]: {
                                  ...editFormData.addresses[billingTab],
                                  houseNo: e.target.value,
                                },
                              },
                            })
                          }
                          className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        />
                      </div>

                      <div>
                        <label className="text-slate-600 font-medium block mb-1">หมู่บ้าน/อาคาร</label>
                        <input
                          type="text"
                          value={editFormData.addresses[billingTab].villageBuilding}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              addresses: {
                                ...editFormData.addresses,
                                [billingTab]: {
                                  ...editFormData.addresses[billingTab],
                                  villageBuilding: e.target.value,
                                },
                              },
                            })
                          }
                          className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        />
                      </div>

                      <div>
                        <label className="text-slate-600 font-medium block mb-1">หมู่ที่</label>
                        <input
                          type="text"
                          value={editFormData.addresses[billingTab].moo}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              addresses: {
                                ...editFormData.addresses,
                                [billingTab]: {
                                  ...editFormData.addresses[billingTab],
                                  moo: e.target.value,
                                },
                              },
                            })
                          }
                          className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        />
                      </div>

                      <div>
                        <label className="text-slate-600 font-medium block mb-1">ซอย</label>
                        <input
                          type="text"
                          value={editFormData.addresses[billingTab].soi}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              addresses: {
                                ...editFormData.addresses,
                                [billingTab]: {
                                  ...editFormData.addresses[billingTab],
                                  soi: e.target.value,
                                },
                              },
                            })
                          }
                          className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="text-slate-600 font-medium block mb-1">ถนน</label>
                        <input
                          type="text"
                          value={editFormData.addresses[billingTab].road}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              addresses: {
                                ...editFormData.addresses,
                                [billingTab]: {
                                  ...editFormData.addresses[billingTab],
                                  road: e.target.value,
                                },
                              },
                            })
                          }
                          className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="text-slate-600 font-medium block mb-1">แขวง/ตำบล</label>
                        <input
                          type="text"
                          value={editFormData.addresses[billingTab].subDistrict}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              addresses: {
                                ...editFormData.addresses,
                                [billingTab]: {
                                  ...editFormData.addresses[billingTab],
                                  subDistrict: e.target.value,
                                },
                              },
                            })
                          }
                          className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="text-slate-600 font-medium block mb-1">เขต/อำเภอ</label>
                        <input
                          type="text"
                          value={editFormData.addresses[billingTab].district}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              addresses: {
                                ...editFormData.addresses,
                                [billingTab]: {
                                  ...editFormData.addresses[billingTab],
                                  district: e.target.value,
                                },
                              },
                            })
                          }
                          className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="text-slate-600 font-medium block mb-1">จังหวัด</label>
                        <input
                          type="text"
                          value={editFormData.addresses[billingTab].province}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              addresses: {
                                ...editFormData.addresses,
                                [billingTab]: {
                                  ...editFormData.addresses[billingTab],
                                  province: e.target.value,
                                },
                              },
                            })
                          }
                          className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="text-slate-600 font-medium block mb-1">รหัสไปรษณีย์</label>
                        <input
                          type="text"
                          value={editFormData.addresses[billingTab].zipcode}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              addresses: {
                                ...editFormData.addresses,
                                [billingTab]: {
                                  ...editFormData.addresses[billingTab],
                                  zipcode: e.target.value,
                                },
                              },
                            })
                          }
                          className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Card 2: แนบเอกสารประกอบ (โฉนด) (Image 4) */}
                  <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs space-y-3">
                    <div className="space-y-0.5">
                      <h4 className="font-bold text-slate-800 text-xs sm:text-sm">แนบเอกสารประกอบ</h4>
                      <p className="text-[11px] text-slate-400">
                        รองรับไฟล์ประเภท png, jpg, jpeg เท่านั้น และไฟล์ขนาดไม่เกิน 10 MB
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-slate-50 text-sky-700 border border-sky-300 rounded-lg text-xs font-semibold cursor-pointer shadow-2xs transition-colors">
                        <Paperclip className="w-3.5 h-3.5 text-sky-600" />
                        <span>แนบโฉนด</span>
                        <input
                          type="file"
                          accept=".png,.jpg,.jpeg,.pdf,.doc,.docx"
                          onChange={handleAddDeedFile}
                          className="hidden"
                        />
                      </label>
                      <span className="text-[10px] text-slate-400 hidden sm:inline">
                        รองรับไฟล์ประเภท PDF, DOC, PNG, JPG และไฟล์ขนาดไม่เกิน 10 MB
                      </span>
                    </div>

                    {/* Deed Files List */}
                    <div className="space-y-1.5 text-xs pt-1">
                      {editFormData.deedFiles.map((file, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-2 rounded-lg bg-slate-50 border border-slate-100 text-slate-700"
                        >
                          <span className="flex items-center gap-2 truncate text-slate-600">
                            <span className="w-6 h-6 rounded bg-emerald-100 text-emerald-700 text-[10px] font-bold flex items-center justify-center shrink-0">
                              PNG
                            </span>
                            <span className="truncate">{file}</span>
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveDeedFile(idx)}
                            className="text-slate-400 hover:text-rose-600 p-1 cursor-pointer"
                            title="ลบไฟล์"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Card 3: แนบเอกสารประกอบ (เอกสารอื่นๆ) (Image 4 & 5) */}
                  <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs space-y-3">
                    <div className="space-y-0.5">
                      <h4 className="font-bold text-slate-800 text-xs sm:text-sm">แนบเอกสารประกอบ</h4>
                      <p className="text-[11px] text-slate-400">
                        รองรับไฟล์ประเภท png, jpg, jpeg เท่านั้น และไฟล์ขนาดไม่เกิน 10 MB
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-slate-50 text-sky-700 border border-sky-300 rounded-lg text-xs font-semibold cursor-pointer shadow-2xs transition-colors">
                        <Paperclip className="w-3.5 h-3.5 text-sky-600" />
                        <span>แนบไฟล์</span>
                        <input
                          type="file"
                          accept=".png,.jpg,.jpeg,.pdf"
                          onChange={handleAddOtherFile}
                          className="hidden"
                        />
                      </label>
                      <span className="text-[10px] text-slate-400 hidden sm:inline">
                        รองรับไฟล์ประเภท png, jpg, jpeg เท่านั้น และไฟล์ขนาดไม่เกิน 10 MB
                      </span>
                    </div>

                    {/* Other Files List */}
                    <div className="space-y-1.5 text-xs pt-1">
                      {editFormData.otherFiles.map((file, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-2 rounded-lg bg-slate-50 border border-slate-100 text-slate-700"
                        >
                          <span className="flex items-center gap-2 truncate text-slate-600">
                            <span
                              className={`w-6 h-6 rounded text-[10px] font-bold flex items-center justify-center shrink-0 ${
                                file.endsWith('.pdf')
                                  ? 'bg-rose-100 text-rose-700'
                                  : 'bg-emerald-100 text-emerald-700'
                              }`}
                            >
                              {file.endsWith('.pdf') ? 'PDF' : 'PNG'}
                            </span>
                            <span className="truncate">{file}</span>
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveOtherFile(idx)}
                            className="text-slate-400 hover:text-rose-600 p-1 cursor-pointer"
                            title="ลบไฟล์"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Card 4: นโยบายการรับประกัน & นโยบายทางการตลาด (Image 5) */}
                  <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs text-xs space-y-4">
                    <div>
                      <div className="flex items-center justify-between text-slate-400 font-medium mb-1 border-b border-slate-100 pb-1">
                        <span>นโยบายการรับประกัน</span>
                        <span>จนถึงวันที่</span>
                      </div>
                      <div className="flex items-center justify-between text-slate-700 font-medium py-1">
                        <span>-</span>
                        <span>-</span>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-slate-100">
                      <div className="flex items-center justify-between text-slate-400 font-medium mb-1 border-b border-slate-100 pb-1">
                        <span>นโยบายทางการตลาด</span>
                        <span>จนถึงวันที่</span>
                      </div>
                      <div className="flex items-center justify-between text-slate-700 font-medium py-1">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editFormData.marketingFeeEnabled}
                            onChange={(e) =>
                              setEditFormData({ ...editFormData, marketingFeeEnabled: e.target.checked })
                            }
                            className="rounded text-sky-600 focus:ring-sky-500"
                          />
                          <span>ค่าส่วนกลาง / common fee</span>
                        </label>
                        <input
                          type="text"
                          value={editFormData.marketingFeeUntilDate}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, marketingFeeUntilDate: e.target.value })
                          }
                          className="border border-slate-200 rounded px-2 py-0.5 text-xs text-slate-800 text-right w-28 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Bottom Save & Cancel Controls */}
                  <div className="flex items-center justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
                    >
                      ยกเลิก
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveEdit}
                      className="px-5 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-xs font-semibold flex items-center gap-2 shadow-xs transition-colors cursor-pointer"
                    >
                      <Save className="w-4 h-4" /> บันทึกข้อมูล
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 2: ผู้อยู่อาศัย (Residents) matching image 12, 13, 14, 18, 19 */}
        {/* ========================================================================= */}
        {activeTab === 'residents' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {residentsList.map((res) => (
                <div
                  key={res.id}
                  className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs hover:shadow-md transition-all flex flex-col justify-between relative group"
                >
                  <div className="space-y-3">
                    {/* Top Row: Avatar & Roles */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div className="w-12 h-12 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500 overflow-hidden font-bold">
                            {res.avatarUrl ? (
                              <img
                                src={res.avatarUrl}
                                alt={res.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <User className="w-6 h-6 text-slate-400" />
                            )}
                          </div>
                          {res.isOnline && (
                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-white absolute bottom-0 right-0" />
                          )}
                        </div>

                        <div>
                          <div className="flex flex-wrap items-center gap-1.5 mb-1">
                            <span
                              className={`text-[10px] px-2 py-0.5 rounded font-semibold ${
                                res.roleType === 'เจ้าของห้อง'
                                  ? 'bg-teal-50 text-teal-800 border border-teal-200'
                                  : res.roleType === 'ผู้เช่า'
                                  ? 'bg-slate-700 text-white'
                                  : 'bg-slate-100 text-slate-700'
                              }`}
                            >
                              {res.roleType}
                            </span>
                            {res.isCommittee && (
                              <span className="text-[10px] px-2 py-0.5 rounded font-semibold bg-sky-500 text-white">
                                กรรมการ
                              </span>
                            )}
                          </div>
                          <h4 className="font-bold text-slate-800 text-xs sm:text-sm">
                            {res.name}
                          </h4>
                          {res.username && (
                            <span className="text-[11px] text-slate-400 block">
                              Username : {res.username}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Top Right Action buttons */}
                      <div className="flex items-center gap-2 text-slate-400 opacity-80 group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingResident(res);
                            setShowResidentModal(true);
                          }}
                          className="hover:text-sky-600 p-1"
                          title="แก้ไข"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setResidentsList(residentsList.filter((r) => r.id !== res.id))
                          }
                          className="hover:text-rose-600 p-1"
                          title="ลบ"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Middle Info Details matching screenshot */}
                    <div className="space-y-1.5 text-xs text-slate-600 pt-2 border-t border-slate-100">
                      <div className="flex items-center gap-2">
                        <span className="w-4 text-slate-400 text-center">🚩</span>
                        <span className="font-medium text-slate-700">{res.nationality}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-4 text-slate-400 text-center">🪪</span>
                        <span>{res.idCardNo}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span>{res.phone}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span className="truncate">{res.email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span>วันที่เข้าโครงการ : {res.joinDate}</span>
                      </div>
                    </div>
                  </div>

                  <div className="text-[11px] text-rose-600 pt-2 font-medium">
                    หมายเหตุ : {res.notes || '-'}
                  </div>
                </div>
              ))}

              {/* Dashed Add Resident Card */}
              <button
                type="button"
                onClick={() => {
                  setEditingResident(null);
                  setShowResidentModal(true);
                }}
                className="rounded-2xl border-2 border-dashed border-slate-300 hover:border-sky-500 hover:bg-sky-50/30 p-8 flex flex-col items-center justify-center gap-2 text-sky-600 font-bold text-xs transition-all cursor-pointer min-h-[220px]"
              >
                <Plus className="w-6 h-6" />
                <span>เพิ่มผู้อยู่อาศัย</span>
              </button>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 3: คีย์การ์ด (Keycards) matching image 13, 27, 28 */}
        {/* ========================================================================= */}
        {activeTab === 'keycards' && (
          <div className="space-y-6">
            <div>
              <h3 className="font-bold text-slate-800 text-sm mb-4">คีย์การ์ดปัจจุบัน</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {keycardsList.map((card) => (
                  <div
                    key={card.id}
                    className="bg-white rounded-2xl border border-slate-200/90 p-6 shadow-2xs hover:border-sky-300 transition-all text-center flex flex-col items-center justify-center space-y-1 relative group"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setKeycardsList(keycardsList.filter((k) => k.id !== card.id))
                      }
                      className="absolute top-3 right-3 text-slate-300 hover:text-rose-500 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <span className="text-lg font-bold text-slate-800 tracking-wider">
                      {card.cardNumber}
                    </span>
                    <span className="text-xs text-slate-500">{card.cardType}</span>
                  </div>
                ))}

                {/* Dashed Add Keycard Button */}
                <button
                  type="button"
                  onClick={() => setShowAddKeycardModal(true)}
                  className="rounded-2xl border-2 border-dashed border-slate-300 hover:border-sky-500 hover:bg-sky-50/30 p-6 flex flex-col items-center justify-center gap-1.5 text-sky-600 font-bold text-xs transition-all cursor-pointer min-h-[100px]"
                >
                  <Plus className="w-5 h-5" />
                  <span>เพิ่มคีย์การ์ด</span>
                </button>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-200">
              <h3 className="font-bold text-slate-600 text-xs mb-3">คีย์การ์ดก่อนหน้า</h3>
              <div className="text-xs text-slate-400 italic bg-slate-50 p-4 rounded-xl border border-slate-100 text-center">
                ไม่มีประวัติคีย์การ์ดก่อนหน้า
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 4: ยานพาหนะ (Vehicles) matching image 14, 29, 30 */}
        {/* ========================================================================= */}
        {activeTab === 'vehicles' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {vehiclesList.map((veh) => (
                <div
                  key={veh.id}
                  className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs hover:shadow-md transition-all flex flex-col justify-between space-y-4 relative group"
                >
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="px-2.5 py-1 rounded bg-teal-600 text-white text-[11px] font-bold">
                        {veh.vehicleType}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setVehiclesList(vehiclesList.filter((v) => v.id !== veh.id))
                        }
                        className="text-slate-300 hover:text-rose-500 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-base font-bold text-slate-800">
                          {veh.plateNumber}
                        </div>
                        <div className="text-xs text-slate-500">{veh.province}</div>
                      </div>

                      <div className="text-right text-xs">
                        <span className="text-slate-400 block text-[10px]">ลักษณะรถ</span>
                        <span className="font-semibold text-slate-700">
                          {veh.brand} {veh.model}
                        </span>
                      </div>
                    </div>

                    {/* Logo / Badge in middle */}
                    <div className="flex items-center justify-center py-2">
                      <div className="w-14 h-14 rounded-full border border-slate-200 flex items-center justify-center bg-slate-50 shadow-2xs">
                        <Car className="w-7 h-7 text-sky-600" />
                      </div>
                    </div>

                    <div className="space-y-2 text-xs text-slate-600 pt-2 border-t border-slate-100">
                      <div className="flex justify-between">
                        <span className="text-slate-400">เลขสติ๊กเกอร์จอดรถ</span>
                        <span className="font-medium text-slate-800">{veh.stickerNo}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">คิดค่าที่จอดรถ</span>
                        <span className="font-medium text-slate-800">
                          {veh.hasParkingFee ? 'มี' : 'ไม่มี'}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-100 text-center">
                        <div className="bg-slate-50 p-1.5 rounded-lg">
                          <span className="text-slate-400 block text-[10px]">ชั้นจอด</span>
                          <span className="font-bold text-slate-800">{veh.floorParking}</span>
                        </div>
                        <div className="bg-slate-50 p-1.5 rounded-lg">
                          <span className="text-slate-400 block text-[10px]">ช่องที่จอด</span>
                          <span className="font-bold text-slate-800">{veh.slotParking}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Dashed Add Vehicle Button */}
              <button
                type="button"
                onClick={() => setShowAddVehicleModal(true)}
                className="rounded-2xl border-2 border-dashed border-slate-300 hover:border-sky-500 hover:bg-sky-50/30 p-8 flex flex-col items-center justify-center gap-2 text-sky-600 font-bold text-xs transition-all cursor-pointer min-h-[220px]"
              >
                <Plus className="w-6 h-6" />
                <span>เพิ่มยานพาหนะ</span>
              </button>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 5: สัตว์เลี้ยง (Pets) matching image 15, 31, 32 */}
        {/* ========================================================================= */}
        {activeTab === 'pets' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {petsList.map((pet) => (
                <div
                  key={pet.id}
                  className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs hover:shadow-md transition-all flex items-start gap-4 relative group"
                >
                  <div className="w-16 h-16 rounded-full bg-slate-100 border border-slate-200 overflow-hidden shrink-0 flex items-center justify-center text-slate-400 font-bold">
                    <Heart className="w-7 h-7 text-rose-400" />
                  </div>

                  <div className="flex-1 min-w-0 space-y-1.5 text-xs">
                    <div className="flex items-center justify-between">
                      <h4 className="font-bold text-slate-800 text-sm truncate">{pet.name}</h4>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingPet(pet);
                          setShowPetModal(true);
                        }}
                        className="text-slate-300 hover:text-sky-600 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    <p className="text-slate-600 text-[11px] line-clamp-2 leading-relaxed">
                      {pet.description}
                    </p>

                    <div className="pt-1">
                      <span className="text-slate-400 text-[10px] block mb-0.5">
                        ใบตรวจสุขภาพประจำปี
                      </span>
                      {pet.healthCertPdf && pet.healthCertPdf !== '-' ? (
                        <div className="flex items-center gap-1.5 text-rose-600 font-medium truncate">
                          <FileText className="w-3.5 h-3.5 shrink-0" />
                          <span className="truncate text-[11px]">{pet.healthCertPdf}</span>
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Dashed Add Pet Button */}
              <button
                type="button"
                onClick={() => {
                  setEditingPet(null);
                  setShowPetModal(true);
                }}
                className="rounded-2xl border-2 border-dashed border-slate-300 hover:border-sky-500 hover:bg-sky-50/30 p-6 flex flex-col items-center justify-center gap-1.5 text-sky-600 font-bold text-xs transition-all cursor-pointer min-h-[120px]"
              >
                <Plus className="w-5 h-5" />
                <span>เพิ่มสัตว์เลี้ยง</span>
              </button>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 6: กรรมสิทธิ์ (Ownership History) matching image 16, 33, 35 */}
        {/* ========================================================================= */}
        {activeTab === 'ownership' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
              <span className="text-slate-500 font-medium">
                วันที่โอนกรรมสิทธิ์ล่าสุด :{' '}
                <strong className="text-slate-800 font-bold">2022-02-18</strong>
              </span>

              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <span className="text-slate-500">วันที่</span>
                  <input
                    type="date"
                    className="px-2.5 py-1 bg-white border border-slate-300 rounded text-xs text-slate-700"
                  />
                  <span className="text-slate-500">ถึง</span>
                  <input
                    type="date"
                    className="px-2.5 py-1 bg-white border border-slate-300 rounded text-xs text-slate-700"
                  />
                </div>

                <div className="relative">
                  <input
                    type="text"
                    placeholder="ค้นหา"
                    className="pl-3 pr-7 py-1 bg-white border border-slate-300 rounded text-xs text-slate-700 focus:outline-none"
                  />
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2" />
                </div>
              </div>
            </div>

            <div className="border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
              <table className="w-full text-xs text-left border-collapse">
                <thead>
                  <tr className="bg-slate-100/90 text-slate-700 font-medium border-b border-slate-200">
                    <th className="py-2.5 px-4 whitespace-nowrap">วันที่โอนกรรมสิทธิ์ ▼</th>
                    <th className="py-2.5 px-3 text-center whitespace-nowrap">ห้อง</th>
                    <th className="py-2.5 px-4 whitespace-nowrap">ผู้โอนกรรมสิทธิ์</th>
                    <th className="py-2.5 px-4 whitespace-nowrap">ผู้รับโอนกรรมสิทธิ์</th>
                    <th className="py-2.5 px-4 whitespace-nowrap">บันทึกโดย</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  {ownershipHistories.map((row) => (
                    <tr key={row.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3 px-4 text-slate-800 font-medium whitespace-nowrap">
                        {row.transferDate}
                      </td>
                      <td className="py-3 px-3 text-center font-medium text-slate-700">
                        {row.roomNumber}
                      </td>
                      <td className="py-3 px-4 text-slate-800">
                        <div className="space-y-0.5">
                          {row.transferor.map((name, i) => (
                            <div key={i}>{name}</div>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-800">
                        <div className="space-y-0.5">
                          {row.transferee.map((name, i) => (
                            <div key={i}>{name}</div>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-600 whitespace-nowrap">
                        {row.recordedBy}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 7: ประวัติผู้อยู่อาศัย (History Logs) matching image 17, 36, 26 */}
        {/* ========================================================================= */}
        {activeTab === 'history_logs' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-end gap-3 text-xs">
              <div className="flex items-center gap-1.5">
                <span className="text-slate-500">วันที่:</span>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="px-2.5 py-1 bg-white border border-slate-300 rounded text-xs text-slate-700"
                />
                <span className="text-slate-500">ถึง:</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="px-2.5 py-1 bg-white border border-slate-300 rounded text-xs text-slate-700"
                />
              </div>

              <div className="relative">
                <input
                  type="text"
                  placeholder="ค้นหา"
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  className="pl-3 pr-7 py-1 bg-white border border-slate-300 rounded text-xs text-slate-700 focus:outline-none"
                />
                <Search className="w-3.5 h-3.5 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2" />
              </div>
            </div>

            <div className="border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-100/90 text-slate-700 font-medium border-b border-slate-200">
                      <th className="py-2.5 px-3 text-center whitespace-nowrap">▲ ลำดับ</th>
                      <th className="py-2.5 px-4 whitespace-nowrap">ชื่อ-นามสกุล</th>
                      <th className="py-2.5 px-3 text-center whitespace-nowrap">สัญชาติ</th>
                      <th className="py-2.5 px-3 text-center whitespace-nowrap">เริ่มเข้าอาศัย</th>
                      <th className="py-2.5 px-3 text-center whitespace-nowrap">วันที่ออก</th>
                      <th className="py-2.5 px-4 whitespace-nowrap">สาเหตุที่ออก</th>
                      <th className="py-2.5 px-3 whitespace-nowrap">เบอร์โทร</th>
                      <th className="py-2.5 px-4 whitespace-nowrap">E-mail</th>
                      <th className="py-2.5 px-3 text-center whitespace-nowrap">ประเภท</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700">
                    {residentHistories
                      .filter(
                        (item) =>
                          !historySearch ||
                          item.name.toLowerCase().includes(historySearch.toLowerCase()) ||
                          item.email.toLowerCase().includes(historySearch.toLowerCase()) ||
                          item.phone.includes(historySearch)
                      )
                      .map((h) => (
                        <tr key={h.id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="py-3 px-3 text-center whitespace-nowrap">
                            <div className="flex items-center justify-center gap-1.5">
                              {h.isLatest && (
                                <span className="px-1.5 py-0.5 rounded-full bg-rose-500 text-white text-[9px] font-bold">
                                  ล่าสุด
                                </span>
                              )}
                              <span>{h.orderNo}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sky-600 font-medium hover:underline cursor-pointer">
                            {h.name}
                          </td>
                          <td className="py-3 px-3 text-center font-medium text-slate-700">
                            {h.nationality}
                          </td>
                          <td className="py-3 px-3 text-center text-slate-700">{h.startDate}</td>
                          <td className="py-3 px-3 text-center text-slate-700">{h.endDate}</td>
                          <td className="py-3 px-4 text-slate-600">{h.reason}</td>
                          <td className="py-3 px-3 text-slate-700">{h.phone}</td>
                          <td className="py-3 px-4 text-slate-600">{h.email}</td>
                          <td className="py-3 px-3 text-center">
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                                h.residentType === 'ผู้เช่า'
                                  ? 'bg-slate-600 text-white'
                                  : 'bg-slate-100 text-slate-700'
                              }`}
                            >
                              {h.residentType}
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {/* Table Footer */}
              <div className="bg-white px-4 py-3 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
                <div className="flex items-center gap-2">
                  <span>แสดง 10 รายการ/หน้า</span>
                </div>
                <div className="flex items-center gap-1">
                  <span>รายการทั้งหมด 140</span>
                  <div className="flex items-center gap-1 ml-4">
                    <button className="px-2 py-0.5 rounded border border-sky-600 bg-sky-50 text-sky-700 font-bold">
                      1
                    </button>
                    <button className="px-2 py-0.5 rounded border border-slate-200 text-slate-600">
                      2
                    </button>
                    <button className="px-2 py-0.5 rounded border border-slate-200 text-slate-600">
                      3
                    </button>
                    <button className="px-2 py-0.5 rounded border border-slate-200 text-slate-600">
                      4
                    </button>
                    <button className="px-2 py-0.5 rounded border border-slate-200 text-slate-600">
                      5
                    </button>
                    <span>...</span>
                    <button className="px-2 py-0.5 rounded border border-slate-200 text-slate-600">
                      14
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ========================================================================= */}
      {/* MODAL 1: เพิ่ม/แก้ไขผู้อยู่อาศัย (matching image 17, 16, 15, 34) */}
      {/* ========================================================================= */}
      {showResidentModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white w-full max-w-4xl rounded-2xl shadow-xl border border-slate-200 overflow-hidden my-8 animate-in fade-in zoom-in-95 duration-150">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
              <h3 className="font-bold text-slate-800 text-sm">
                {editingResident ? 'แก้ไขข้อมูลผู้อยู่อาศัย' : 'เพิ่มผู้อยู่อาศัย'}
              </h3>
              <button
                type="button"
                onClick={() => setShowResidentModal(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Form body matching images 17, 16, 15, 34 */}
            <div className="p-6 space-y-6 text-xs text-slate-700 max-h-[75vh] overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left side: ข้อมูลผู้อยู่อาศัย */}
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400">
                      <User className="w-8 h-8" />
                    </div>
                    <button
                      type="button"
                      className="px-3 py-1.5 border border-sky-500 text-sky-600 hover:bg-sky-50 rounded-lg text-xs font-semibold"
                    >
                      เปลี่ยนรูปภาพ
                    </button>
                  </div>

                  {/* Role Type Radio */}
                  <div className="flex flex-wrap items-center gap-4 pt-2">
                    <label className="flex items-center gap-1.5 cursor-pointer font-medium">
                      <input
                        type="radio"
                        name="resRole"
                        defaultChecked
                        className="text-sky-600 focus:ring-sky-500"
                      />
                      เจ้าของห้อง
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="resRole"
                        className="text-sky-600 focus:ring-sky-500"
                      />
                      ผู้อยู่อาศัย
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="resRole"
                        className="text-sky-600 focus:ring-sky-500"
                      />
                      ผู้เช่า
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="resRole"
                        className="text-sky-600 focus:ring-sky-500"
                      />
                      ตัวแทน
                    </label>
                  </div>

                  {/* Name fields */}
                  <div>
                    <label className="block text-slate-600 font-semibold mb-1">
                      คำนำหน้าชื่อ *
                    </label>
                    <select className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs">
                      <option>นาย</option>
                      <option>นาง</option>
                      <option>นางสาว</option>
                      <option>Mr.</option>
                      <option>Mrs.</option>
                      <option>Miss</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-slate-600 font-semibold mb-1">
                        ชื่อ (ไทย) *
                      </label>
                      <input
                        type="text"
                        defaultValue={editingResident ? editingResident.name : 'ทดลอง'}
                        className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-600 font-semibold mb-1">
                        นามสกุล (ไทย) *
                      </label>
                      <input
                        type="text"
                        defaultValue="เทส"
                        className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-slate-600 font-semibold mb-1">Name (Eng) *</label>
                      <input
                        type="text"
                        defaultValue="ทดลอง"
                        className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-600 font-semibold mb-1">
                        Surname (Eng) *
                      </label>
                      <input
                        type="text"
                        defaultValue="เทส"
                        className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-slate-600 font-semibold mb-1">อาชีพ</label>
                    <input
                      type="text"
                      className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs"
                    />
                  </div>

                  {/* Marital status */}
                  <div>
                    <label className="block text-slate-600 font-semibold mb-1.5">สถานภาพ</label>
                    <div className="flex flex-wrap items-center gap-3">
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="radio" name="marital" defaultChecked /> ไม่ระบุ
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="radio" name="marital" /> โสด
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="radio" name="marital" /> สมรส
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="radio" name="marital" /> หย่าร้าง
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="radio" name="marital" /> หม้าย
                      </label>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-slate-600 font-semibold mb-1">สัญชาติ</label>
                      <select className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs">
                        <option>Thai</option>
                        <option>AF</option>
                        <option>CH</option>
                        <option>SG</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-slate-600 font-semibold mb-1">เชื้อชาติ</label>
                      <select className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs">
                        <option>Thai</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-slate-600 font-semibold mb-1">วันเกิด</label>
                      <input
                        type="date"
                        className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-600 font-semibold mb-1">เพศ</label>
                      <select className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs">
                        <option>กรุณาเลือก</option>
                        <option>ชาย</option>
                        <option>หญิง</option>
                        <option>ไม่ระบุ</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-slate-600 font-semibold mb-1">
                      วันที่เข้าโครงการ *
                    </label>
                    <input
                      type="date"
                      defaultValue="2024-03-29"
                      className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 font-semibold mb-1">หมายเหตุ</label>
                    <textarea
                      rows={2}
                      className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs"
                    />
                  </div>
                </div>

                {/* Right side: Verification & Documents */}
                <div className="space-y-4">
                  {/* Verification Box */}
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                    <span className="text-slate-700 font-bold block">
                      สถานะการยืนยันตัวตนแอปพลิเคชัน :{' '}
                      <span className="text-slate-500 font-normal">ยังไม่ยืนยันตัวตน</span>
                    </span>

                    <div className="grid grid-cols-3 gap-2">
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="radio" name="idType" /> บัตรประชาชน
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="radio" name="idType" /> บัตรข้าราชการ
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="radio" name="idType" /> ใบขับขี่
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="radio" name="idType" /> Passport
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="radio" name="idType" /> อื่นๆ
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="radio" name="idType" defaultChecked /> ไม่ระบุ
                      </label>
                    </div>

                    <input
                      type="text"
                      placeholder="ระบุเลขบัตร / เลขที่ผู้เสียภาษี"
                      className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs"
                    />

                    <button
                      type="button"
                      className="px-3 py-1.5 border border-sky-500 text-sky-600 rounded-lg text-xs font-semibold flex items-center gap-1.5"
                    >
                      <Upload className="w-3.5 h-3.5" /> แนบไฟล์
                    </button>
                  </div>

                  {/* Attachment Documents */}
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2.5">
                    <span className="text-slate-700 font-bold block">แนบเอกสารประกอบ</span>
                    <span className="text-[10px] text-slate-400 block mb-2">
                      รองรับไฟล์ประเภท png, jpg และ jpeg เท่านั้นและไฟล์ขนาดไม่เกิน 10 MB
                    </span>

                    {[
                      '1. Passport',
                      '2. เอกสารสัญญาเช่า',
                      '3. สัญญาระหว่างผู้เช่ากับเจ้าของห้อง',
                      '4. แบบฟอร์มรายวัน (การเปลี่ยนคีย์การ์ด)',
                    ].map((docName, idx) => (
                      <div key={idx} className="flex items-center justify-between py-1">
                        <span className="text-slate-700">{docName}</span>
                        <button
                          type="button"
                          className="px-3 py-1 border border-sky-500 text-sky-600 rounded-lg text-[11px] font-semibold flex items-center gap-1"
                        >
                          <Upload className="w-3 h-3" /> แนบไฟล์
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Contacts */}
                  <div className="space-y-3">
                    <div>
                      <label className="block text-slate-600 font-semibold mb-1">
                        เบอร์โทรศัพท์ที่ติดต่อได้
                      </label>
                      <input
                        type="text"
                        placeholder="080-000-0000"
                        className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-600 font-semibold mb-1">
                        เบอร์โทรศัพท์ที่ติดต่อได้ (สำรอง)
                      </label>
                      <input
                        type="text"
                        className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-600 font-semibold mb-1">อีเมล</label>
                      <input
                        type="email"
                        placeholder="user@example.com"
                        className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer buttons */}
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowResidentModal(false)}
                className="px-4 py-2 border border-slate-300 text-slate-700 hover:bg-slate-100 rounded-xl font-medium text-xs transition-colors cursor-pointer"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                onClick={() => setShowResidentModal(false)}
                className="px-6 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-xl font-bold text-xs transition-colors cursor-pointer"
              >
                บันทึก
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 2: เพิ่มคีย์การ์ดใหม่ (matching image 28) */}
      {/* ========================================================================= */}
      {showAddKeycardModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-xl border border-slate-200 p-6 space-y-4 animate-in fade-in zoom-in-95">
            <h3 className="font-bold text-slate-800 text-sm">เพิ่มคีย์การ์ดใหม่</h3>

            <form onSubmit={handleSaveKeycard} className="space-y-3 text-xs">
              <div>
                <label className="block text-slate-600 font-semibold mb-1">ประเภทคีย์การ์ด</label>
                <input
                  type="text"
                  placeholder="เช่น เข้าห้อง, Parking Car"
                  value={newKeycardType}
                  onChange={(e) => setNewKeycardType(e.target.value)}
                  className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs"
                />
              </div>

              <div>
                <label className="block text-slate-600 font-semibold mb-1">เลขคีย์การ์ด *</label>
                <input
                  type="text"
                  required
                  placeholder="เช่น 1234567"
                  value={newKeycardNumber}
                  onChange={(e) => setNewKeycardNumber(e.target.value)}
                  className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs"
                />
              </div>

              <div className="flex items-center gap-2 pt-2">
                <button
                  type="submit"
                  className="flex-1 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-xl font-bold text-xs transition-colors cursor-pointer"
                >
                  บันทึกข้อมูล
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddKeycardModal(false)}
                  className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl font-medium text-xs transition-colors cursor-pointer"
                >
                  ยกเลิก
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 3: เพิ่มยานพาหนะ (matching image 30) */}
      {/* ========================================================================= */}
      {showAddVehicleModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white w-full max-w-3xl rounded-2xl shadow-xl border border-slate-200 p-6 space-y-6 my-8 animate-in fade-in zoom-in-95">
            <h3 className="font-bold text-slate-800 text-sm">ข้อมูลยานพาหนะ</h3>

            <form onSubmit={handleSaveVehicle} className="space-y-6 text-xs">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left side */}
                <div className="space-y-3">
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-1.5 cursor-pointer font-medium">
                      <input
                        type="radio"
                        name="vehType"
                        checked={newVehType === 'รถยนต์'}
                        onChange={() => setNewVehType('รถยนต์')}
                      />
                      รถยนต์
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="vehType"
                        checked={newVehType === 'รถจักรยานยนต์'}
                        onChange={() => setNewVehType('รถจักรยานยนต์')}
                      />
                      รถจักรยานยนต์
                    </label>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-slate-600 font-semibold mb-1">
                        เลขทะเบียน *
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="เช่น 1กข 1234"
                        value={newVehPlate}
                        onChange={(e) => setNewVehPlate(e.target.value)}
                        className="w-full p-2 bg-white border border-slate-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-600 font-semibold mb-1">จังหวัด</label>
                      <select
                        value={newVehProv}
                        onChange={(e) => setNewVehProv(e.target.value)}
                        className="w-full p-2 bg-white border border-slate-300 rounded-lg"
                      >
                        <option>กรุงเทพมหานคร</option>
                        <option>ชลบุรี</option>
                        <option>นนทบุรี</option>
                        <option>ปทุมธานี</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-slate-600 font-semibold mb-1">ยี่ห้อ</label>
                    <input
                      type="text"
                      placeholder="เช่น Toyota, Honda, BMW"
                      value={newVehBrand}
                      onChange={(e) => setNewVehBrand(e.target.value)}
                      className="w-full p-2 bg-white border border-slate-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 font-semibold mb-1">รุ่น</label>
                    <input
                      type="text"
                      placeholder="เช่น Civic, Camry, 4 Series"
                      value={newVehModel}
                      onChange={(e) => setNewVehModel(e.target.value)}
                      className="w-full p-2 bg-white border border-slate-300 rounded-lg"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-slate-600 font-semibold mb-1">ปี</label>
                      <input
                        type="text"
                        placeholder="2023"
                        value={newVehYear}
                        onChange={(e) => setNewVehYear(e.target.value)}
                        className="w-full p-2 bg-white border border-slate-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-600 font-semibold mb-1">
                        ขนาด (ที่นั่ง)
                      </label>
                      <input
                        type="text"
                        placeholder="4"
                        value={newVehSeats}
                        onChange={(e) => setNewVehSeats(e.target.value)}
                        className="w-full p-2 bg-white border border-slate-300 rounded-lg"
                      />
                    </div>
                  </div>
                </div>

                {/* Right side */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-slate-600 font-semibold mb-1">
                      เลขสติ๊กเกอร์จอดรถ
                    </label>
                    <input
                      type="text"
                      placeholder="เช่น AVC4456689"
                      value={newVehSticker}
                      onChange={(e) => setNewVehSticker(e.target.value)}
                      className="w-full p-2 bg-white border border-slate-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <span className="block text-slate-600 font-semibold mb-1">
                      ตำแหน่งช่องจอด
                    </span>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-slate-400 text-[10px] mb-0.5">ชั้นจอด</label>
                        <input
                          type="text"
                          placeholder="2"
                          value={newVehFloor}
                          onChange={(e) => setNewVehFloor(e.target.value)}
                          className="w-full p-2 bg-white border border-slate-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-400 text-[10px] mb-0.5">
                          ช่องที่จอด
                        </label>
                        <input
                          type="text"
                          placeholder="10"
                          value={newVehSlot}
                          onChange={(e) => setNewVehSlot(e.target.value)}
                          className="w-full p-2 bg-white border border-slate-300 rounded-lg"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-slate-600 font-semibold mb-1">
                      วันที่เริ่มใช้งาน
                    </label>
                    <input
                      type="date"
                      value={newVehDate}
                      onChange={(e) => setNewVehDate(e.target.value)}
                      className="w-full p-2 bg-white border border-slate-300 rounded-lg"
                    />
                  </div>

                  <label className="flex items-center gap-2 pt-2 cursor-pointer font-medium text-slate-700">
                    <input
                      type="checkbox"
                      checked={newVehFee}
                      onChange={(e) => setNewVehFee(e.target.checked)}
                      className="rounded text-sky-600"
                    />
                    คิดค่าที่จอดรถ
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddVehicleModal(false)}
                  className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl font-medium text-xs transition-colors cursor-pointer"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-xl font-bold text-xs transition-colors cursor-pointer"
                >
                  บันทึกข้อมูล
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 4: สัตว์เลี้ยง (matching image 32) */}
      {/* ========================================================================= */}
      {showPetModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-xl border border-slate-200 p-6 space-y-4 animate-in fade-in zoom-in-95">
            <h3 className="font-bold text-slate-800 text-sm">
              {editingPet ? 'Edit pet details' : 'ประเภทสัตว์เลี้ยง'}
            </h3>

            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400">
                <Heart className="w-8 h-8 text-rose-400" />
              </div>
              <div>
                <span className="text-slate-600 font-semibold block mb-1">รูปภาพ</span>
                <button
                  type="button"
                  className="text-sky-600 hover:underline font-medium text-xs"
                >
                  เปลี่ยนรูปภาพ
                </button>
              </div>
            </div>

            <div className="space-y-3 text-xs">
              <div>
                <label className="block text-slate-600 font-semibold mb-1">ประเภทสัตว์เลี้ยง</label>
                <input
                  type="text"
                  placeholder="เช่น สุนัข, แมว, นก"
                  defaultValue={editingPet ? editingPet.petType : ''}
                  className="w-full p-2 bg-white border border-slate-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-slate-600 font-semibold mb-1">
                  ลักษณะเพิ่มเติม (เช่น สี, ตำหนิ)
                </label>
                <textarea
                  rows={2}
                  placeholder="ระบุลักษณะ..."
                  defaultValue={editingPet ? editingPet.description : ''}
                  className="w-full p-2 bg-white border border-slate-300 rounded-lg"
                />
              </div>

              <div>
                <span className="block text-slate-600 font-semibold mb-1">ใบตรวจสุขภาพประจำปี</span>
                <button
                  type="button"
                  className="px-3 py-1.5 border border-sky-500 text-sky-600 rounded-lg text-xs font-semibold flex items-center gap-1.5"
                >
                  <Upload className="w-3.5 h-3.5" /> แนบไฟล์
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowPetModal(false)}
                className="flex-1 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl font-medium text-xs transition-colors cursor-pointer"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                onClick={() => setShowPetModal(false)}
                className="flex-1 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-xl font-bold text-xs transition-colors cursor-pointer"
              >
                บันทึกข้อมูล
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 5: เปลี่ยนแปลงกรรมสิทธิ์ (matching image 35) */}
      {/* ========================================================================= */}
      {showTransferModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-xl border border-slate-200 p-6 space-y-6 my-8 animate-in fade-in zoom-in-95">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <button
                  type="button"
                  onClick={() => setShowTransferModal(false)}
                  className="text-slate-400 hover:text-slate-700"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <h3 className="font-bold text-slate-800 text-base">เปลี่ยนแปลงกรรมสิทธิ์</h3>
              </div>
              <p className="text-xs text-rose-500">
                *หากทำการเปลี่ยนแปลงกรรมสิทธิ์แล้ว, รายชื่อผู้อยู่อาศัยในห้อง จะถูกลบออกทั้งหมด
              </p>
            </div>

            <div className="space-y-4 text-xs text-slate-700 max-h-[65vh] overflow-y-auto pr-1">
              {/* ข้อมูลโอนกรรมสิทธิ์ */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                <h4 className="font-bold text-slate-800">ข้อมูลโอนกรรมสิทธิ์</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-slate-400 block mb-0.5">เลขที่ห้อง</span>
                    <span className="font-bold text-slate-800">{roomNumber}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block mb-0.5">ขนาดพื้นที่ (ตร. ม.)</span>
                    <span className="font-bold text-slate-800">125.30</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-slate-400 block mb-0.5">รายชื่อเจ้าของกรรมสิทธิ์</span>
                    <span className="font-bold text-slate-800">วีรวิช ทิพยชัยวรุตม์</span>
                  </div>
                </div>
              </div>

              {/* ข้อมูลผู้รับโอนกรรมสิทธิ์ */}
              <div className="space-y-3">
                <h4 className="font-bold text-slate-800">ข้อมูลผู้รับโอนกรรมสิทธิ์</h4>

                <div>
                  <label className="block text-slate-600 font-semibold mb-1">
                    วันที่โอนกรรมสิทธิ์ *
                  </label>
                  <input
                    type="date"
                    defaultValue="2024-03-29"
                    className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400">
                    <User className="w-7 h-7" />
                  </div>
                  <button
                    type="button"
                    className="px-3 py-1.5 border border-sky-500 text-sky-600 rounded-lg text-xs font-semibold"
                  >
                    เปลี่ยนรูปภาพ
                  </button>
                </div>

                <div>
                  <label className="block text-slate-600 font-semibold mb-1">คำนำหน้าชื่อ *</label>
                  <select className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs">
                    <option>นาย</option>
                    <option>นาง</option>
                    <option>นางสาว</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-600 font-semibold mb-1">ชื่อ (ไทย) *</label>
                    <input
                      type="text"
                      className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-600 font-semibold mb-1">
                      นามสกุล (ไทย) *
                    </label>
                    <input
                      type="text"
                      className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-600 font-semibold mb-1">สัญชาติ</label>
                    <select className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs">
                      <option>Thai</option>
                      <option>Foreigner</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-600 font-semibold mb-1">เชื้อชาติ</label>
                    <select className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs">
                      <option>Thai</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-600 font-semibold mb-1">เพศ</label>
                    <select className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs">
                      <option>ไม่ระบุ</option>
                      <option>ชาย</option>
                      <option>หญิง</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-600 font-semibold mb-1">สถานภาพ</label>
                    <select className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs">
                      <option>ไม่ระบุ</option>
                      <option>โสด</option>
                      <option>สมรส</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-slate-600 font-semibold mb-1">
                    เบอร์โทรศัพท์ที่ติดต่อได้
                  </label>
                  <input
                    type="text"
                    placeholder="080-000-0000"
                    className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowTransferModal(false)}
                className="px-4 py-2 border border-slate-300 text-slate-700 hover:bg-slate-100 rounded-xl font-medium text-xs transition-colors cursor-pointer"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                onClick={() => setShowTransferModal(false)}
                className="px-6 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-xl font-bold text-xs transition-colors cursor-pointer"
              >
                บันทึกการโอน
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal: Single Unit Passcode Card & QR Code for Print / Save */}
      {showSinglePasscodeModal && (
        <MemberPasscodePrintModal
          singleRoomNumber={roomNumber}
          residentRole={selectedPasscodeRole}
          onClose={() => setShowSinglePasscodeModal(false)}
        />
      )}
    </div>
  );
};
